import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CheckCircle, ExpandMore, Settings } from "@mui/icons-material";
import {
  Box,
  Button,
  buttonClasses,
  ClickAwayListener,
  IconButton,
  inputClasses,
  List,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  outlinedInputClasses,
  Paper,
  Popper,
  Stack,
  styled,
  Typography as T,
  Tooltip,
  useTheme,
} from "@mui/material";
import FileSelect from "datasets/pages/tables/components/FileSelect";
import SourceFileConfigurationModal from "datasets/pages/tables/components/SourceFileConfigurationModal";
import FileValidationModal from "datasets/pages/tables/components/file-validation/FileValidationModal";
import useMetadataContext from "datasets/pages/tables/contexts/metadata";
import useFileMetadata from "datasets/pages/tables/hooks/useFileMetadata";
import useSnowflakeSchemas from "datasets/pages/tables/hooks/useSnowflakeSchemas";
import { MetadataMode } from "datasets/pages/tables/types/enums/metadata";
import { Ingest } from "datasets/pages/tables/types/interfaces/ingest";
import { RESERVED_KEYWORDS } from "datasets/pages/tables/utils/reserved-keywords";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import yn from "yn";
import { SearchIcon } from "~/assets/icons";
import TextOverflow from "~/components/TextOverflow";

const { VITE_DATASET_INGESTION_JOBS_ENABLED } = import.meta.env;

export const StyledOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
  gap: 4,
  padding: 8,
  [`.${inputClasses.inputSizeSmall}`]: {
    height: "unset",
    lineHeight: "20px",
    padding: 0,
  },
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: theme.palette.grey[300],
  },
}));

export const StyledLoadingButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.outlinedPrimary}`]: {
    borderColor: theme.palette.grey[300],
    justifyContent: "space-between",
    lineHeight: "20px",
    padding: 8,
    "&:hover": {
      backgroundColor: "#FFFFFF",
      borderColor: theme.palette.primary.dark,
    },
  },
  [`& .${buttonClasses.endIcon}`]: {
    color: theme.palette.text.primary,
  },
  [`& .${buttonClasses.loadingIndicator}`]: {
    color: theme.palette.primary.dark,
  },
}));

const FileMetadataSuccessText = ({ sourceFileKey }: { sourceFileKey: string }) => {
  const { watch } = useFormContext<Ingest>();
  const sourceMetadataKey = watch("metadata_platform_storage_key");
  const { data: fileMetadata } = useFileMetadata(sourceMetadataKey);
  const metadataFileIdx = sourceMetadataKey.lastIndexOf("/");

  const metadataKeyFromSourceFile = `${sourceFileKey}.metadata.json`;
  const hasFoundMetadataFileFromSourceFile = sourceMetadataKey === metadataKeyFromSourceFile;

  if (!fileMetadata) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <CheckCircle sx={{ width: 16, height: 16, color: "success.main" }} />
      <T variant="caption" sx={{ opacity: 0.6 }}>
        {`${
          hasFoundMetadataFileFromSourceFile ? "Found" : "Using"
        } metadata file: "${sourceMetadataKey.slice(metadataFileIdx + 1)}"`}
      </T>
    </Box>
  );
};

const SourceSelect = () => {
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [fileValidationModalOpen, setFileValidationModalOpen] = useState(false);
  const { resetField, reset, setValue, watch, getValues } = useFormContext<Ingest>();
  const { setErrors, setMode } = useMetadataContext();
  const sourceFileKey = watch("platform_storage_key");
  const sourceMetadataKey = watch("metadata_platform_storage_key");
  const [popperAnchorEl, setPopperAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<null | FileInfo>(null);
  const [browsingFiles, setIsBrowsingFiles] = useState(false);

  const {
    data: fileMetadata,
    error: fileError,
    isLoading: loadingFile,
  } = useFileMetadata(sourceMetadataKey);

  const handlePopperClose = () => {
    setSelectedFile(null);
    setPopperAnchorEl(null);
  };

  const handleFileSelectClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (yn(VITE_DATASET_INGESTION_JOBS_ENABLED)) {
      // Open new validation modal
      setFileValidationModalOpen(true);
    } else {
      // Use existing file browser
      setPopperAnchorEl(event.currentTarget);
      setIsBrowsingFiles(true);
    }
  };

  const handleFileValidated = (file: FileInfo, validationMetadata: any) => {
    // TODO: Handle validated file data when modal is implemented
    setFileValidationModalOpen(false);
  };

  // When a file is selected, update the value of `metadata_platform_storage_key`.
  // This will trigger a fetch to determine if a matching metadata.json file exists.
  const handleSelectSourceFile = () => {
    // reset ingest errors
    setErrors([]);
    if (selectedFile) {
      // Reset the entire form to clear any previous metadata
      const currentValues = getValues();
      reset({
        ...currentValues,
        platform_storage_key: "",
        metadata_platform_storage_key: "",
        metadata_config: {
          columns: [],
          database_schema: "",
          table_description: "",
          table: "",
          tags: [],
        },
      });

      // Clear the metadata context mode
      setMode(undefined);

      // Then set the new file values
      const metadataKey = `${selectedFile.key}.metadata.json`;
      setValue("file_size", selectedFile.size);
      setValue("metadata_platform_storage_key", metadataKey);
      setIsBrowsingFiles(false);
    }
  };

  // Wait for a response from file metadata before closing popper
  useEffect(() => {
    if (!browsingFiles && selectedFile) {
      if (fileMetadata) {
        // Populate form values from fetched metadata
        setValue("platform_storage_key", selectedFile.key);
        setValue("metadata_config.columns", fileMetadata.columns);
        setValue("metadata_config.database_schema", fileMetadata.database_schema);
        setValue("metadata_config.file_format", fileMetadata.file_format);
        setValue("metadata_config.table", fileMetadata.table.toUpperCase().replace(" ", "_"), {
          shouldValidate: true,
        });
        setValue("metadata_config.table_description", fileMetadata.table_description);
        setValue("metadata_config.tags", fileMetadata.tags);

        // Set metadata context mode to file
        setMode(MetadataMode.FILE);
        handlePopperClose();
      } else if (fileError) {
        // Just set the platform storage key and close
        setValue("platform_storage_key", selectedFile.key);
        // Remove metadata storage key if not found
        resetField("metadata_platform_storage_key");
        handlePopperClose();
      }
    }
  }, [fileError, fileMetadata, browsingFiles, selectedFile, setValue, setMode, resetField]);

  return (
    <Stack gap={0.5} width="100%">
      <T variant="body2" fontWeight={500}>
        Source File
      </T>
      <Box alignItems="center" display="flex" gap={1}>
        <Box sx={{ flex: 1, position: "relative" }}>
          <StyledLoadingButton
            fullWidth
            size="small"
            onClick={handleFileSelectClick}
            endIcon={!yn(VITE_DATASET_INGESTION_JOBS_ENABLED) && <ExpandMore />}
            variant="outlined"
          >
            <TextOverflow
              variant="body2"
              maxWidth={560}
              sx={{
                opacity: sourceFileKey ? 1 : 0.6,
                textAlign: "left",
              }}
            >
              {sourceFileKey ? resolveRouterPath(sourceFileKey) : "Select source file..."}
            </TextOverflow>
          </StyledLoadingButton>
          <FileSelect
            popperAnchorEl={popperAnchorEl}
            handlePopperClose={handlePopperClose}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            onSelectFile={handleSelectSourceFile}
            loading={loadingFile}
          />
        </Box>
        <Tooltip arrow title="Edit configuration" enterDelay={500}>
          <span>
            <IconButton
              disabled={!sourceFileKey}
              sx={{
                height: 39,
                width: 39,
              }}
              onClick={() => setMetadataModalOpen(true)}
            >
              <Settings />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <FileMetadataSuccessText sourceFileKey={sourceFileKey} />
      {metadataModalOpen && (
        <SourceFileConfigurationModal
          open={metadataModalOpen}
          onClose={() => setMetadataModalOpen(false)}
          metadataConfig={getValues("metadata_config")}
          setIngestFormValue={setValue}
          metadataFileName={fileMetadata ? sourceMetadataKey : ""}
        />
      )}
      {yn(VITE_DATASET_INGESTION_JOBS_ENABLED) && fileValidationModalOpen && (
        <FileValidationModal
          open={fileValidationModalOpen}
          onClose={() => setFileValidationModalOpen(false)}
          onFileValidated={handleFileValidated}
        />
      )}
    </Stack>
  );
};

const SchemaSelect = () => {
  const theme = useTheme();
  const fieldKey = "metadata_config.database_schema";
  const { watch, setValue } = useFormContext<Ingest>();
  const value = watch(fieldKey);
  const { data: schemas, isLoading } = useSnowflakeSchemas();
  const [popperAnchorEl, setPopperAnchorEl] = useState<null | HTMLElement>(null);
  const [schemaSearchTerm, setSchemaSearchTerm] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");

  const filteredSchemas = schemas?.filter((schema) =>
    schema.name.toLowerCase().includes(schemaSearchTerm.toLowerCase())
  );

  const handleSchemasOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setPopperAnchorEl(event.currentTarget);
  };

  const handleSchemasClose = () => {
    setPopperAnchorEl(null);
    setSchemaSearchTerm("");
  };

  return (
    <Stack gap={0.5} width="100%">
      <T variant="body2" fontWeight={500}>
        Destination
      </T>
      <StyledLoadingButton
        size="small"
        endIcon={<ExpandMore />}
        loading={isLoading}
        loadingPosition="end"
        onClick={handleSchemasOpen}
        sx={{ justifyContent: "space-between" }}
        variant="outlined"
      >
        <T fontSize="inherit" sx={{ opacity: value ? 1 : 0.5 }}>
          {value ? value : "Select destination..."}
        </T>
      </StyledLoadingButton>
      <T variant="caption" sx={{ opacity: 0.5 }}>
        Imported data will be stored as a new table within the selected destination.
      </T>
      <Popper
        disablePortal
        anchorEl={popperAnchorEl}
        open={!!popperAnchorEl}
        placement="bottom-start"
        sx={{ width: "100%", zIndex: theme.zIndex.tooltip }}
      >
        <ClickAwayListener onClickAway={handleSchemasClose}>
          <Paper
            sx={{
              boxShadow:
                "0px 1px 5px 0px #0000001F, 0px 2px 2px 0px #00000024, 0px 3px 1px -2px #00000033",
            }}
          >
            <Stack alignItems="flex-end" p={2} gap={1.25}>
              <StyledOutlinedInput
                fullWidth
                autoFocus
                placeholder="Search..."
                size="small"
                startAdornment={<SearchIcon width={16} />}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSchemaSearchTerm(e.target.value)}
              />
              <Stack
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1.5,
                  width: "100%",
                }}
              >
                <List dense disablePadding sx={{ height: 290, overflow: "auto" }}>
                  {filteredSchemas?.map((schema, i) => (
                    <ListItemButton
                      key={i}
                      selected={selectedSchema === schema.name}
                      onClick={() => setSelectedSchema(schema.name)}
                    >
                      <ListItemText>{schema.name}</ListItemText>
                    </ListItemButton>
                  ))}
                </List>
              </Stack>
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={handleSchemasClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setValue(fieldKey, selectedSchema);
                    handleSchemasClose();
                  }}
                >
                  Select
                </Button>
              </Box>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
};

const ImportDetails = ({ onNextPanel }: { onNextPanel: () => void }) => {
  const { control, getFieldState, watch } = useFormContext<Ingest>();
  const tableField = getFieldState("metadata_config.table");
  const storageKey = watch("platform_storage_key");
  const destination = watch("metadata_config.database_schema");
  const tableName = watch("metadata_config.table");

  return (
    <>
      <SourceSelect />
      <SchemaSelect />
      <Stack gap={0.5} width="100%">
        <Controller
          control={control}
          name="metadata_config.table"
          rules={{
            required: "Table name is required.",
            validate: {
              correctStart: (value: string) =>
                /^[A-Za-z_]/.test(value) ? true : 'Must start with a letter or underscore "_".',
              validChars: (value: string) =>
                /^[A-Za-z0-9_$]*$/.test(value)
                  ? true
                  : 'Can only contain letters, numbers, "_" or "$".',
              notReserved: (value: string) =>
                !RESERVED_KEYWORDS.includes(value)
                  ? true
                  : `"${value}" is a reserved keyword and cannot be used.`,
            },
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <>
                <T variant="body2" fontWeight={500}>
                  Table Name
                </T>
                <StyledOutlinedInput
                  {...field}
                  fullWidth
                  error={!!error}
                  placeholder="Enter a display name for this table..."
                  size="small"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const formatted = e.target.value.toUpperCase().replace(/\s+/g, "_");
                    field.onChange(formatted);
                  }}
                />
                {error && (
                  <T variant="caption" color="attention.main">
                    {error.message}
                  </T>
                )}
                <T variant="caption" sx={{ opacity: 0.5 }}>
                  Table name must be in uppercase with no spaces.
                </T>
              </>
            );
          }}
        />
      </Stack>

      <Button
        variant="contained"
        disabled={!(storageKey && destination && tableName) || tableField.invalid}
        onClick={() => onNextPanel()}
      >
        Next
      </Button>
    </>
  );
};

export default ImportDetails;
